import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useAddEmail, useUpdateEmail } from '../hooks'
import { emailSchema, type EmailFormValues } from '../schema'
import type { EmailDto } from '../types'

interface EmailFormSheetProps {
  customerId: number
  email?: EmailDto | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EmailFormSheet({ customerId, email, open, onOpenChange }: EmailFormSheetProps) {
  const isEdit = email != null && email.id != null
  const add = useAddEmail(customerId)
  const update = useUpdateEmail(customerId)

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      value: email?.value ?? '',
      label: email?.label ?? '',
      isPrimary: email?.isPrimary ?? false,
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        value: email?.value ?? '',
        label: email?.label ?? '',
        isPrimary: email?.isPrimary ?? false,
      })
    }
  }, [open, email, form])

  const onSubmit = async (values: EmailFormValues) => {
    try {
      if (isEdit && email?.id != null) {
        await update.mutateAsync({
          emailId: email.id,
          email: { id: email.id, value: values.value, label: values.label || null, isPrimary: values.isPrimary, isActive: true },
        })
        toast.success('Email zaktualizowany')
      } else {
        await add.mutateAsync({ id: null, value: values.value, label: values.label || null, isPrimary: values.isPrimary, isActive: true })
        toast.success('Email dodany')
      }
      onOpenChange(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd'
      toast.error(`Nie udało się zapisać: ${msg}`)
    }
  }

  const submitting = add.isPending || update.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edytuj email' : 'Dodaj email'}</SheetTitle>
          <SheetDescription>Email może być oznaczony jako główny - max jeden aktywny.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adres email *</FormLabel>
                    <FormControl><Input {...field} type="email" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Etykieta</FormLabel>
                    <FormControl><Input {...field} placeholder="prywatny / firmowy" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPrimary"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">Główny email klienta</FormLabel>
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                  Anuluj
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Zapisywanie...' : isEdit ? 'Zapisz' : 'Dodaj'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export function useEmailFormSheet(customerId: number) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<EmailDto | null>(null)
  return {
    openAdd: () => {
      setTarget(null)
      setOpen(true)
    },
    openEdit: (email: EmailDto) => {
      setTarget(email)
      setOpen(true)
    },
    sheet: (
      <EmailFormSheet
        customerId={customerId}
        email={target}
        open={open}
        onOpenChange={setOpen}
      />
    ),
  }
}
